import React, { useMemo, useState, useEffect } from "react";
import MutedText from "../common/MutedText";
import { ToggleSwitch, ShowHide } from "@athena/forge";
import InfoButton from "@athena/forge-icons/dist/InfoButtonSmall";
import { useTranslation } from "react-i18next";
import { useOktaAuth } from "@okta/okta-react";
import { updateMfaPromptEveryLogin } from "../../api/Api";
import { logTwoStepVerificationToggled } from "../../lib/amplitude-utils";

const TwoStepVerification = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { authState } = useOktaAuth();

  const [isChecked, setIsChecked] = useState(false);
  const [showFactorToggle, setShowFactorToggle] =
    useState(false);
  const [isPracticeMfaEnabled, setIsPracticeMfaEnabled] = useState(false);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      if (!authState?.idToken) return;

      try {
        const tokenPayload = JSON.parse(
          atob(authState.idToken.idToken.split(".")[1]),
        );

        const mfaPromptEveryLogin = tokenPayload.mfaPromptEveryLogin;
        setIsChecked(!!mfaPromptEveryLogin);

        const mfaPromptHighMedRiskContexts =
          tokenPayload.mfaPromptHighMedRiskContexts || [];
        const mfaPromptHighRiskContexts =
          tokenPayload.mfaPromptHighRiskContexts || [];
        const isUserInAdjacentServiceGroup = 
          tokenPayload.isAdjacentServicesUser || false;
        const isUserInHighRiskContexts =
          Array.isArray(mfaPromptHighMedRiskContexts) &&
          mfaPromptHighMedRiskContexts.length > 0;
        const isUserInHighAndMediumRiskContexts =
          Array.isArray(mfaPromptHighRiskContexts) &&
          mfaPromptHighRiskContexts.length > 0;
        const isPracticeMfaEnabled =
          isUserInHighRiskContexts || isUserInHighAndMediumRiskContexts;

        setIsPracticeMfaEnabled(isPracticeMfaEnabled);
        setShowFactorToggle(!isUserInAdjacentServiceGroup);
      } catch (error) {
        setIsChecked(false);
        setShowFactorToggle(false);
      }
    };

    initialize();
  }, [authState]);

  const handleToggleChange = async (): Promise<void> => {
    if (!authState) return;

    try {
      const newCheckedValue = !isChecked;
      await updateMfaPromptEveryLogin(authState, newCheckedValue);
      setIsChecked(newCheckedValue);
      logTwoStepVerificationToggled(newCheckedValue);
    } catch (error) {
      setIsChecked(prevState => !!prevState);
    }
  };

  const title = useMemo(() => t("twostepverification.page.title"), [t]);

  const mutedText = useMemo(
    () => t("twostepverification.page.muted.text"),
    [t],
  );

  const mutedTextOptional = useMemo(
    () => t("twostepverification.page.muted.text.optional"),
    [t],
  );

  return (
    <ShowHide show={showFactorToggle}>
      <section className="two-step-verification-section">
        <div className="uiam-user-profile-section-container">
          <div className="fe_u_margin--bottom-small">{title}</div>
          <ToggleSwitch
            checked={isChecked}
            onChange={handleToggleChange}
            aria-label="two-step-verification-toggle-label"
            data-testid="two-step-verification-toggle-test-id"
          />
        </div>
        <div className="uiam-factor-toggle-description">
          {isPracticeMfaEnabled && (
            <div className="uiam-factor-toggle-info-icon">
              <InfoButton
                id="two-step-verification-info-icon"
                aria-label="two-step-verification-toggle-icon-label"
                data-testid="two-step-verification-toggle-icon-test-id"
              />
            </div>
          )}
          <MutedText
            className="fe_u_font-size--small fe_u_text-align--left"
            text={isPracticeMfaEnabled ? mutedText : mutedTextOptional}
            data-testid={isPracticeMfaEnabled ? "two-step-verification-toggle-text-test-id" : "two-step-verification-toggle-text-optional-id"}
          />
        </div>
      </section>
    </ShowHide>
  );
};

export default TwoStepVerification;
